import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { TicketDetail, TicketWorkEntry, TicketWorkLog } from "@kundeninfo/api-client";
import { Icon } from "@kundeninfo/ui";
import { Abschnitt, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import { keepInView } from "../../scroll.js";
import { Avatar } from "./Avatar.js";
import { effortTotalsOf, hasEffort, shownEffort } from "./effort.js";
import { EFFORT_TEXT, effortLines } from "./effortTexts.js";
import { WorkEntryPanel } from "./WorkEntryPanel.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DER ABSCHNITT „AUFWAND“ (Spec Aufwand A19): im Slot `abschnitt` des Ticketdialogs unter „Teilaufgaben“. Zähler „Plan · Ist“
 * (mit Teilaufgaben „Σ …“ über den Teilbaum), zu Beginn offen nur mit eigenen Buchungen; „Aufwand buchen“ in den Aktionen.
 * Die Buchungen lädt der Abschnitt beim ersten Aufklappen genau einmal; danach führt er die Liste aus den Antworten von
 * Buchen und Löschen (`TicketWorkLog`) — eine solche Antwort ersetzt das erste Laden (es entfällt bzw. sein späteres
 * Ergebnis bleibt ungenutzt). Zähler und Kopfzeilen kommen immer aus dem Detail `d` (R1): Der Dialog führt es nach
 * eigenen Buchungen (`onNeu`, P3) und nach Schreiben im Kind-Dialog (`reloadToken`); aus `log` kommt nur die Liste. Kopfzeilen aus `effortLines(…, "dialog")`, die Tabelle mit Datum, Person, Stunden
 * (rechtsbündig mit Einheit), Bemerkung und „Löschen“ an eigenen Buchungen (ADMIN: an allen).
 *
 * Nach Buchen/Löschen meldet der Abschnitt das Detail mit den Summen der Antwort (`onNeu`, P3) — der Dialog übernimmt sie
 * sofort und holt den Verlauf nach. Vor jeder Übernahme merkt er die Lage seines Kopfs und gleicht nach dem Zeichnen aus
 * (Hausregel „Scroll bei Neuzeichnen“, `keepInView`). Das Panel steht neben dem Abschnitt, nicht in seinem Inhalt: Der
 * Knopf in den Aktionen öffnet es auch bei zugeklapptem Abschnitt.
 */

export type EffortSectionProps = {
  d: TicketDetail; client: TicketsClient; ich: { id: string; istAdmin: boolean };
  onNeu: (d: TicketDetail) => void;
};

type Panel = { kind: "book" } | { kind: "delete"; entry: TicketWorkEntry };

export function EffortSection(p: EffortSectionProps): ReactElement {
  const d = p.d;
  const [offen, setOffen] = useState(d.workedMinutes > 0);
  const [log, setLog] = useState<TicketWorkLog | null>(null);
  const [ladefehler, setLadefehler] = useState(false);
  const [panel, setPanel] = useState<Panel | null>(null);
  const [gone, setGone] = useState(false);
  const geladen = useRef(false);
  const wurzel = useRef<HTMLElement | null>(null);
  const ausgleich = useRef<(() => void) | null>(null);
  useLayoutEffect(() => { ausgleich.current?.(); ausgleich.current = null; });
  const { client } = p;
  useEffect(() => {
    if (!offen || geladen.current) return;
    geladen.current = true;
    // Kam inzwischen die Antwort einer Buchung, ist sie neuer als dieses Laden — sie bleibt stehen.
    client.listTicketWorkEntries(d.id).then((l) => setLog((da) => da ?? l), () => setLadefehler(true));
  }, [offen, client, d.id]);

  // R1: Summen immer aus dem Detail — aus `log` kommt nur die Liste.
  const s = shownEffort(d);
  const darfLoeschen = (e: TicketWorkEntry) => e.user.id === p.ich.id || p.ich.istAdmin;

  function fertig(neu: TicketWorkLog, schonWeg: boolean): void {
    if (wurzel.current !== null) ausgleich.current = keepInView(wurzel.current);
    // Die Antwort ist die volle Liste: Das Aufklappen danach lädt nicht mehr (und überschreibt sie nicht).
    geladen.current = true;
    setLog(neu);
    setPanel(null);
    setOffen(true);
    setGone(schonWeg);
    p.onNeu({ ...d, ...effortTotalsOf(neu) });
  }

  return (
    <>
      <Abschnitt ref={wurzel} className="tw" titel={EFFORT_TEXT.section} icon={<Icon name="clock" size={16} />}
        {...(hasEffort(s) ? { zaehler: EFFORT_TEXT.mark(s.plan, s.worked, s.sum) } : {})}
        offen={offen} onUmgeschaltet={(e) => setOffen(e.offen)}
        aktionen={<KiKnopf art="leise" kompakt onClick={() => setPanel({ kind: "book" })}>{EFFORT_TEXT.book}</KiKnopf>}>
        {ladefehler && log === null && <Hinweis art="fehler">{EFFORT_TEXT.failLoad}</Hinweis>}
        {gone && <Hinweis art="info">{EFFORT_TEXT.alreadyGone}</Hinweis>}
        {effortLines(s, "dialog").map((z) => <p key={z} className="tw__kopfzeile">{z}</p>)}
        {log !== null && (log.items.length === 0 ? <p className="tw__leer">{EFFORT_TEXT.empty}</p> : (
          <Tabelle stapeln dicht>
            <table>
              <thead><tr>
                <th scope="col">{EFFORT_TEXT.columns.date}</th><th scope="col">{EFFORT_TEXT.columns.person}</th>
                <th scope="col" data-ausrichtung="rechts">{EFFORT_TEXT.columns.hours}</th>
                <th scope="col">{EFFORT_TEXT.columns.note}</th>
                <th scope="col"><span className="bt__unsichtbar">{EFFORT_TEXT.panelDelete}</span></th>
              </tr></thead>
              <tbody>
                {log.items.map((e) => (
                  <tr key={e.id}>
                    <td>{EFFORT_TEXT.day(e.workDate)}</td>
                    <td><Avatar person={e.user} ich={e.user.id === p.ich.id} /></td>
                    <td data-ausrichtung="rechts">{EFFORT_TEXT.hours(e.minutes)}</td>
                    <td className="tw__bemerkung">{e.note ?? ""}</td>
                    <td>{darfLoeschen(e) && (
                      <LoeschKnopf name={EFFORT_TEXT.deleteName(e.workDate, e.minutes)}
                        onClick={() => setPanel({ kind: "delete", entry: e })} />
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Tabelle>
        ))}
      </Abschnitt>
      {panel !== null && (
        <WorkEntryPanel {...panel} d={d} client={client} onDone={fertig} onAbbrechen={() => setPanel(null)} />
      )}
    </>
  );
}
